import {
  Alert,
  Button,
  Card,
  Group,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageTitle, QueryState } from '../../ui/components';
import { useWorkplace } from '../access';
import { unwrap, useDb } from '../supabase';
import { AdminOnly } from './AdminOnly';

/** IMO number: 7 digits with the check digit (same rule as the database, D45). */
export function isValidImo(imo: string): boolean {
  if (!/^\d{7}$/.test(imo)) {
    return false;
  }
  const digits = imo.split('').map(Number);
  const sum = digits.slice(0, 6).reduce((total, d, i) => total + d * (7 - i), 0);
  return sum % 10 === digits[6];
}

/** A2 Vessels (Admin): create with IMO, enrol in RISQ (creates one item per question). */
export function AdminVesselsPage() {
  const { t } = useTranslation('dashboard');
  const db = useDb();
  const client = useQueryClient();
  const { access } = useWorkplace();
  const vessels = useQuery({
    queryKey: ['admin', 'vessels'],
    enabled: access.isAdmin,
    queryFn: async () => {
      const rows = unwrap(
        await db
          .from('vessels')
          .select('id, company_id, name, imo, vessel_type, flag')
          .is('archived_at', null)
          .order('name'),
      );
      const counts = await Promise.all(
        rows.map(async (v) => {
          const { count } = await db
            .from('risq_vessel_items')
            .select('id', { count: 'exact', head: true })
            .eq('vessel_id', v.id);
          return [v.id, count ?? 0] as const;
        }),
      );
      return { rows, items: new Map(counts) };
    },
  });
  const [form, setForm] = useState({
    companyId: '',
    name: '',
    imo: '',
    type: 'Bulk carrier',
    flag: '',
  });
  const refresh = () =>
    Promise.all([
      client.invalidateQueries({ queryKey: ['admin', 'vessels'] }),
      client.invalidateQueries({ queryKey: ['access'] }),
    ]);
  const create = useMutation({
    mutationFn: async () =>
      unwrap(
        await db
          .from('vessels')
          .insert({
            company_id: form.companyId,
            name: form.name.trim(),
            imo: form.imo.trim(),
            vessel_type: form.type.trim() === '' ? null : form.type.trim(),
            flag: form.flag.trim() === '' ? null : form.flag.trim(),
          })
          .select('id'),
      ),
    onSuccess: async () => {
      setForm({ ...form, name: '', imo: '', flag: '' });
      await refresh();
    },
  });
  const enroll = useMutation({
    mutationFn: async (vesselId: string) =>
      unwrap(await db.rpc('enroll_vessel_in_risq', { p_vessel_id: vesselId })),
    onSuccess: refresh,
  });
  const imoOk = isValidImo(form.imo.trim());
  const companyName = (id: string) => access.companies.find((c) => c.id === id)?.name ?? '';
  const error = create.error ?? enroll.error;
  return (
    <AdminOnly>
      <PageTitle title={t('admin.vesselsTitle')} />
      <Stack gap="lg">
        <Card withBorder>
          <Stack>
            <Title order={2} size="h3">
              {t('admin.newVessel')}
            </Title>
            <Group align="flex-end">
              <Select
                label={t('admin.company')}
                data={access.companies.map((c) => ({ value: c.id, label: c.name }))}
                value={form.companyId === '' ? null : form.companyId}
                onChange={(value) => setForm({ ...form, companyId: value ?? '' })}
              />
              <TextInput
                label={t('admin.vesselName')}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.currentTarget.value })}
              />
              <TextInput
                label={t('admin.imo')}
                value={form.imo}
                error={form.imo !== '' && !imoOk ? t('admin.imoInvalid') : undefined}
                onChange={(e) => setForm({ ...form, imo: e.currentTarget.value })}
              />
              <TextInput
                label={t('admin.vesselType')}
                value={form.type}
                onChange={(e) => setForm({ ...form, type: e.currentTarget.value })}
              />
              <TextInput
                label={t('admin.flag')}
                value={form.flag}
                onChange={(e) => setForm({ ...form, flag: e.currentTarget.value })}
              />
              <Button
                disabled={form.companyId === '' || form.name.trim() === '' || !imoOk}
                loading={create.isPending}
                onClick={() => create.mutate()}
              >
                {t('admin.create')}
              </Button>
            </Group>
          </Stack>
        </Card>
        {error === null ? null : (
          <Alert color="red" role="alert">
            {t('common.saveFailed', { message: error.message })}
          </Alert>
        )}
        <QueryState loading={vessels.isPending} error={vessels.error}>
          {() => (
            <Table striped>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('admin.vesselName')}</Table.Th>
                  <Table.Th>{t('admin.company')}</Table.Th>
                  <Table.Th>{t('admin.imo')}</Table.Th>
                  <Table.Th>{t('admin.risqItems')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(vessels.data?.rows ?? []).map((v) => {
                  const count = vessels.data?.items.get(v.id) ?? 0;
                  return (
                    <Table.Tr key={v.id}>
                      <Table.Td>
                        <Text fw={600}>{v.name}</Text>
                      </Table.Td>
                      <Table.Td>{companyName(v.company_id)}</Table.Td>
                      <Table.Td>{v.imo}</Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <Text size="sm">{t('admin.items', { count })}</Text>
                          <Button
                            size="xs"
                            variant="default"
                            loading={enroll.isPending && enroll.variables === v.id}
                            onClick={() => enroll.mutate(v.id)}
                          >
                            {count === 0 ? t('admin.enroll') : t('admin.enrollAgain')}
                          </Button>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </QueryState>
      </Stack>
    </AdminOnly>
  );
}
